<template>
  <div
    v-loading="!questionnaire"
    class="tw-flex tw-flex-col tw-min-h-screen"
  >
    <div
      v-if="questionnaire"
      class="tw-fixed tw-z-10 tw-flex tw-flex-col tw-w-64 tw-pr-4"
    >
      <div class="tw-flex tw-flex-col tw-w-64 tw-pr-4 tw-gap-1">
        <div
          v-for="(chapter, index) in questionnaire.survey_json?.chapters ?? []"
          :key="chapter.id"
          class="tw-transition-colors tw-p-1 tw-flex tw-gap-2 tw-w-full tw-justify-between tw-items-center tw-text-dark"
          :class="[
            questionnaire.survey_json.errors[chapter.id] ? 'tw-text-primary' : '',
            !canNavigateThroughTheChapters(index) ? 'tw-text-primary/60 tw-cursor-not-allowed' : 'tw-cursor-pointer', // does previous chapter has error
            currentChapterTab === chapter.id ? 'tw-font-medium' : '',
          ]"
          @click="openChapter(index)"
        >
          <div>
            {{ $capitalize(chapter.label) }}
          </div>
          <div
            v-if="questionnaire.survey_json.errors[chapter.id]"
            class="tw-h-min"
          >
            <icon-close />
          </div>
          <div
            v-else
            class="tw-text-green-600"
          >
            <icon-check />
          </div>
        </div>
      </div>
      <el-divider />
      <div class="tw-flex tw-flex-col tw-gap-4 tw-px-1">
        <i-badge
          v-if="questionnaire.submitted && $isInRole(['FOREIGNER'])"
          type="success"
          class="tw-w-full tw-text-nowrap tw-text-center tw-py-2"
        >
          {{ $t('quest.already_submitted') }}
        </i-badge>
        <i-button
          v-if="!questionnaire.submitted"
          type="primary"
          @click="updateQuestionnaire"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </div>
    <div
      v-if="questionnaire"
      class="tw-px-4 tw-pl-64 tw-w-full"
    >
      <h1 class="tw-leading-9 tw-mb-2">
        {{ questionnaire.survey_type_name }}
      </h1>
      <el-form
        :model="form"
        class="tw-flex tw-flex-col tw-gap-2"
      >
        <div class="tw-flex tw-flex-col tw-gap-6">
          <div
            v-for="section in currentChapter?.sections"
            :key="section.id"
          >
            <h2 class="tw-text-lg tw-font-medium tw-mb-2">
              {{ $capitalize(section.label) }}
            </h2>
            <div class="tw-flex tw-flex-col tw-gap-2">
              <div
                v-for="question in section.questions"
                :key="question.id"
                class="tw-flex-1"
              >
                <i-form-item
                  v-if="question.type === 'string'"
                  :label="$capitalize(question.label)"
                  :error="getErrorForField(currentChapter.id, section.id, question.id)"
                  :class="[
                    question.value !== null ? 'has-value' : '',
                  ]"
                >
                  <el-input
                    v-if="question.type === 'string'"
                    :id="question.id"
                    v-model="question.value"
                    :disabled="fieldDisabled"
                    placeholder=""
                    @input="debouncedSubmitForm"
                  />
                </i-form-item>

                <i-form-item
                  v-else-if="question.type === 'datetime'"
                  :label="$capitalize(question.label)"
                  :error="getErrorForField(currentChapter.id, section.id, question.id)"
                  :class="[
                    question.value !== null ? 'has-value' : '',
                  ]"
                >
                  <el-date-picker
                    v-model="question.value"
                    type="date"
                    placeholder=""
                    :disabled="fieldDisabled"
                    format="DD.MM.YYYY"
                    value-format="DD.MM.YYYY"
                    style="width: 100%"
                    @input="() => {} /* prevent sending request with broken input (part of the date) */"
                    @change="question.force_refresh ? updateQuestionnaire(false) : debouncedSubmitForm()"
                  />
                </i-form-item>
                <i-form-item
                  v-else-if="question.type === 'integer'"
                  :label="$capitalize(question.label)"
                  :error="getErrorForField(currentChapter.id, section.id, question.id)"
                  :class="[
                    question.value !== null ? 'has-value' : '',
                  ]"
                >
                  <el-select
                    :id="question.id"
                    v-model="question.value"
                    placeholder=""
                    filterable
                    :disabled="fieldDisabled"
                    @change="question.force_refresh ? updateQuestionnaire(false) : debouncedSubmitForm()"
                  >
                    <el-option
                      v-for="item in integerItems"
                      :key="item"
                      :label="item"
                      :value="item"
                    />
                  </el-select>
                </i-form-item>

                <i-form-item
                  v-else-if="question.type.split('_')[0] === 'enum' && question.items?.length > 0"
                  :label="$capitalize(question.label)"
                  :error="getErrorForField(currentChapter.id, section.id, question.id)"
                  :class="[
                    question.value !== null ? 'has-value' : '',
                  ]"
                >
                  <el-select
                    :id="question.id"
                    v-model="question.value"
                    class="placeholder-position-50"
                    placeholder=""
                    :disabled="fieldDisabled"
                    filterable
                    @change="question.force_refresh ? updateQuestionnaire(false) : debouncedSubmitForm()"
                  >
                    <el-option
                      v-for="item in question.items"
                      :key="item.id"
                      :label="item[question.keySelect].toString()"
                      :value="item[question.valueSelect]"
                    />
                  </el-select>
                </i-form-item>

                <i-form-item
                  v-else-if="question.type === 'boolean'"
                  class="tw-relative tw-pb-3"
                  :class="[
                    question.value !== null ? 'has-value' : '',
                  ]"
                >
                  <div class="tw-mb-1">
                    {{ question.label }}
                  </div>

                  <el-radio-group
                    :id="question.id"
                    v-model="question.value"
                    :disabled="fieldDisabled"
                    @change="question.force_refresh ? updateQuestionnaire(false) : debouncedSubmitForm()"
                  >
                    <el-radio :label="false">
                      {{ $t('common.values.no') }}
                    </el-radio>
                    <el-radio :label="true">
                      {{ $t('common.values.yes') }}
                    </el-radio>
                  </el-radio-group>

                  <div class="tw-absolute -tw-bottom-4 tw-left-0 tw-text-primary tw-capitalize tw-text-xs">
                    {{ getErrorForField(currentChapter.id, section.id, question.id) }}
                  </div>
                </i-form-item>
              </div>
            </div>
          </div>
        </div>
      </el-form>
      <div
        v-if="$isInRole(['ADMIN', 'CONSULTANT', 'FOREIGNER']) || ($isInRole(['HR']) && form.case_assessments)"
        class="tw-flex tw-pt-6 tw-pb-2"
        :class="[
          currentChapterIndex === 0 ? 'tw-justify-end' : 'tw-justify-between',
        ]"
      >
        <i-button
          v-if="currentChapterIndex !== 0"
          type="primary"
          :loading="isSavingInProgress"
          @click="openChapter(currentChapterIndex - 1)"
        >
          {{ $t('common.actions.back') }}
        </i-button>
        <i-button
          v-if="currentChapterIndex !== questionnaire?.survey_json?.chapters.length - 1"
          type="primary"
          :disabled="!canPressNext"
          :loading="isSavingInProgress"
          @click="openChapter(currentChapterIndex + 1)"
        >
          {{ $t('common.actions.next') }}
        </i-button>
        <template v-else>
          <i-button
            v-if="isSubmitQuestionnaireButtonShown"
            type="primary"
            :loading="isSavingInProgress"
            @click="isSubmitQuestionnaireDialogOpen = true"
          >
            {{ $t('quest.submit_questionnaire') }}
          </i-button>
        </template>
      </div>
    </div>
    <el-dialog
      :visible="isSubmitQuestionnaireDialogOpen"
      width="844px"
      class="tw-overflow-hidden"
      @close="isSubmitQuestionnaireDialogOpen = false"
    >
      <div class="tw-flex tw-flex-col">
        <h1 class="tw-mb-8">
          {{ $t('quest.submit_questionnaire') }}
        </h1>
        {{ $t('quest.submit_questionnaire_confirm_text') }}
      </div>

      <template #footer>
        <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
          <i-button
            class="tw-w-40"
            @click="isSubmitQuestionnaireDialogOpen = false"
          >
            {{ $t('common.actions.cancel') }}
          </i-button>
          <i-button
            type="primary"
            class="tw-w-40"
            :is-loading="isSavingInProgress"
            @click="updateQuestionnaire(true)"
          >
            {{ $t('common.actions.save') }}
          </i-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { debounce } from 'lodash';

export default {
  name: 'QuestionnairePage',
  data() {
    return {
      currentChapterTab: '',
      form: {},
      isSavingInProgress: false,
      debouncedSubmitForm: () => {},
      isSubmitQuestionnaireDialogOpen: false,
    };
  },
  computed: {
    storePath() {
      return this.$isInRole(['FOREIGNER']) ? 'v2/foreigner/questionnaires' : 'v2/admin/foreigners/foreigner';
    },
    questionnaire() {
      const pathArray = this.storePath.split('/');
      let stateFragment = this.$store.state;
      pathArray.forEach((path) => {
        stateFragment = stateFragment[path];
      });
      return stateFragment.questionnaire;
    },
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      genders: (state) => state.v2.shared.enums.enums.genders,
      countries: (state) => state.v2.shared.enums.enums.countries,
      maritalStatuses: (state) => state.v2.shared.enums.enums.marital_statuses,
      supportSpecification: (state) => state.v2.shared.enums.enums.support_specification,
      supportTimeline: (state) => state.v2.shared.enums.enums.support_timeline,
    }),
    ...mapGetters({
      dialCodes: 'v2/shared/enums/dialCodes',
      integerItems: 'v2/shared/enums/integerItems',
    }),
    currentChapter() {
      return this.form.chapters?.find((chapter) => chapter.id === this.currentChapterTab);
    },
    currentChapterIndex() {
      return this.form.chapters?.findIndex((chapter) => chapter.id === this.currentChapterTab);
    },
    isHrAndCaseAssessmentQuestionnaire() {
      return this.$isInRole(['HR']) && this.questionnaire?.case_assessment;
    },
    isHrAndNotCaseAssessmentQuestionnaire() {
      return this.$isInRole(['HR']) && !this.questionnaire?.case_assessment;
    },
    questionsCount() {
      return this.questionnaire?.survey_json?.chapters?.reduce((acc, chapter) => {
        return acc + chapter.sections.reduce((acc, section) => {
          return acc + section.questions.length;
        }, 0);
      }, 0) ?? 0;
    },
    canPressNext() {
      if (this.$isInRole(['FOREIGNER'])) {
        return !this.questionnaire?.survey_json?.errors?.[this.currentChapterTab];
      } else {
        return this.canNavigateThroughTheChapters(this.currentChapterIndex);
      }
    },
    canNavigateThroughTheChapters() {
      return (index) => {
        const noErrorOnPreviousChapter = !this.questionnaire.survey_json.errors[this.questionnaire.survey_json?.chapters?.[index - 1]?.id];
        return this.$isInRole(['ADMIN', 'CONSULTANT', 'HR', 'AGENCY']) || noErrorOnPreviousChapter;
      };
    },
    isSubmitQuestionnaireButtonShown() {
      return !this.questionnaire.submitted && this.$isInRole(['FOREIGNER', 'ADMIN', 'CONSULTANT']) || this.isHrAndCaseAssessmentQuestionnaire;
    },
    fieldDisabled() {
      return this.questionnaire?.submitted || this.$isInRole(['AGENCY']) || this.isHrAndNotCaseAssessmentQuestionnaire;
    },
    getErrorForField() {
      return (chapterId, sectionId, fieldId) => {
        return (
          this.questionnaire.survey_json?.errors?.[chapterId]?.[sectionId]?.[fieldId]?.[0] ?? ''
        );
      };
    },
  },
  watch: {
    questionnaire: {
      handler() {
        if (!this.questionnaire) return;

        if (this.currentChapterTab === '') {
          const chapters = this.questionnaire.survey_json.chapters;
          const reversedChapters = [...chapters].reverse();
          const reversedIndexWithoutError = reversedChapters.findIndex(
            (chapter) => !this.questionnaire.survey_json.errors[chapter.id],
          );

          if (reversedIndexWithoutError === -1) {
            this.currentChapterTab = chapters[0].id;
          } else {
            const lastValidChapterIndex = chapters.length - 1 - reversedIndexWithoutError;
            const nextChapterIndex = lastValidChapterIndex + 1;

            if (nextChapterIndex < chapters.length) {
              this.currentChapterTab = chapters[nextChapterIndex].id;
            } else {
              this.currentChapterTab = chapters[0].id;
            }
          }
        }
        if (!this.isSavingInProgress) {
          this.generateDataForForm();
        }
      },
      deep: true,
    },
    questionsCount() {
      if (this.questionsCount !== 0) {
        this.generateDataForForm();
      }
    },
  },
  created() {
    const updateQuestionnaireFunction = this.updateQuestionnaire;
    this.debouncedSubmitForm = debounce(function() {
      updateQuestionnaireFunction(false);
    }, 300);
  },
  async mounted() {
    this.$store.dispatch('v2/shared/enums/loadSupportTimeline');
    this.$store.dispatch('v2/shared/enums/loadSupportSpecifications');
    await this.$store.dispatch(`${this.storePath}/loadQuestionnaire`, { questionnaireId: this.$route.params.qid, foreignerId: this.$route.params.fid });
  },
  methods: {
    generateDataForForm() {
      this.form = JSON.parse(JSON.stringify(this.questionnaire.survey_json));

      for (const chapter of this.form.chapters) {
        for (const section of chapter.sections) {
          for (const question of section.questions) {
            this.setupQuestionItems(question);
            if (question.type === 'datetime') {
              this.setupDateTimeQuestion(question);
            }
            question.localError = '';
          }
        }
      }

      this.form.completed = this.questionnaire.completed;
      this.form.case_assessment = this.questionnaire.case_assessment;
    },

    setupQuestionItems(question) {
      if (question.type.split('_')[0] !== 'enum') return;

      const enumKey = question.type;
      switch (enumKey) {
        case 'enum_gender':
          this.setQuestionItems(question, this.genders, 'name', 'id');
          break;
        case 'enum_country':
          this.setQuestionItems(question, this.countries, 'name', 'id');
          break;
        case 'enum_marital_status':
          this.setQuestionItems(question, this.maritalStatuses, 'name', 'id');
          break;
        case 'enum_phone_code':
          this.setQuestionItemsForEnumPhoneCode(question, this.dialCodes, 'codePlus', 'id');
          break;
        case 'enum_support_specification': {
          this.setQuestionItems(question, this.supportSpecification, 'name', 'id');
          break;
        }
        case 'enum_support_timeline': {
          this.setQuestionItems(question, this.supportTimeline, 'name', 'id');
          break;
        }
      }
    },

    setQuestionItems(question, items, keySelect, valueSelect) {
      question.items = items;
      question.keySelect = keySelect;
      question.valueSelect = valueSelect;
    },

    setQuestionItemsForEnumPhoneCode(question, items, keySelect, valueSelect) {
      question.items = items;
      question.keySelect = keySelect;
      question.valueSelect = valueSelect;

      // Handling broken prod data, when value for some reason is string type, not number
      if (typeof question.value === 'string' && question.value.length) {
        question.value = Number(question.value);
      }
    },

    setupDateTimeQuestion(question) {
      question.showDateWidget = false;
      if (question.value.length > 1) {
        question.valueGoodFormat = question.value.slice(0, -9);
        question.value = question.value.slice(0, -9);
      }
    },

    prepareFormObject: function(obj, submitted) {
      const newObj = JSON.parse(JSON.stringify(obj));

      for (const chapter of newObj) {
        for (const section of chapter.sections) {
          for (const question of section.questions) {
            this.cleanUpQuestion(question);
          }
        }
      }

      const final = { survey_json: newObj };
      if (submitted === true) {
        final.submitted = true;
      }
      return final;
    },

    cleanUpQuestion: function(question) {
      const type = question.type.split('_')[0];

      switch (type) {
        case 'enum':
          this.handleEnumQuestion(question);
          break;
        case 'integer':
          this.handleIntegerQuestion(question);
          break;
        case 'datetime':
          this.handleDateTimeQuestion(question);
          break;
        default:
          break;
      }

      this.removeUnnecessaryFields(question);
    },

    handleEnumQuestion(question) {
      delete question.items;
    },

    handleIntegerQuestion(question) {
      if (question.value !== '') {
        question.value = parseInt(question.value);
      }
    },

    handleDateTimeQuestion(question) {
      if (question.value !== '') {
        question.value = question.value + ' 00:00:00';
      }
    },

    removeUnnecessaryFields(question) {
      delete question.label;
      delete question.type;
    },

    getTabIdByIndex(index) {
      return this.questionnaire.survey_json.chapters[index].id;
    },

    async openChapter(index) {
      const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

      if (index < this.currentChapterIndex || this.$isInRole(['ADMIN', 'CONSULTANT', 'HR', 'AGENCY'])) {
        this.currentChapterTab = this.getTabIdByIndex(index);
        scrollToTop();
        return;
      }

      const hasSurveyErrors = this.questionnaire.survey_json?.errors?.[this.currentChapterTab];
      if (!hasSurveyErrors || this.questionnaire?.submitted) {
        scrollToTop();
        this.currentChapterTab = this.getTabIdByIndex(index);
      }
    },

    async updateQuestionnaire(submitted) {
      if (this.$isInRole(['AGENCY']) || this.isHrAndNotCaseAssessmentQuestionnaire) return;
      try {
        this.isSavingInProgress = true;
        await this.$store.dispatch(`${this.storePath}/updateQuestionnaire`, {
          questionnaireId: this.$route.params.qid,
          foreignerId: this.$route.params.fid,
          payload: this.prepareFormObject(this.form.chapters, submitted),
          completed: this.questionnaire?.completed,
        });
        if (submitted) {
          this.isSubmitQuestionnaireDialogOpen = false;
        }
      } catch (e) {

      } finally {
        this.isSavingInProgress = false;
      }
    },
  },
};
</script>
